import { vCPUbyInstanceType } from "@/components/aws";
import { useAvailableRunners } from "@/hooks/use-available-runners";
import { useListEnvironmentClasses, type PlainRunnerEnvironmentClass } from "@/queries/environment-queries";
import { useGetOrganizationPolicies } from "@/queries/organization-queries";
import { type PlainRunner } from "@/queries/runner-queries";
import { RunnerKind } from "gitpod-next-api/gitpod/v1/runner_configuration_pb";
import { RunnerPhase } from "gitpod-next-api/gitpod/v1/runner_pb";
import { useEffect, useState } from "react";

export type EnvironmentClassEntry = {
    clazz: PlainRunnerEnvironmentClass;
    runner: PlainRunner;
    localRunner: boolean;
    policyRestricted: boolean;
};

export function useEnvironmentClassEntries() {
    const { data: policies, isLoading: isLoadingPolicies } = useGetOrganizationPolicies();

    // TODO(at) replace this once the policies are fully rolled out
    // const allowLocalRunners = policies?.allowLocalRunners;
    const allowLocalRunners = policies?.allowLocalRunners;

    const { data: environmentClasses, isLoading: isLoadingClasses } = useListEnvironmentClasses({
        filter: { enabled: true },
    });
    const { availableRunners, isLoading: isLoadingRunners } = useAvailableRunners();

    const [environmentClassEntries, setEnvClasses] = useState<EnvironmentClassEntry[]>([]);

    const [initialized, setInitialized] = useState<boolean>(false);

    useEffect(() => {
        const classes = environmentClasses?.classes;
        if (classes === undefined || isLoadingRunners || isLoadingClasses) {
            return;
        }

        const runnersMap = new Map(availableRunners.map((r) => [r.runnerId, r]));
        const joined: EnvironmentClassEntry[] = [];
        for (const clazz of classes) {
            const runner = runnersMap.get(clazz.runnerId);
            if (runner) {
                const localRunner = runner.kind === RunnerKind.LOCAL;
                const policyRestricted = !allowLocalRunners && localRunner;

                joined.push({ clazz, runner, localRunner, policyRestricted });
            }
        }

        setEnvClasses(joined);
        setInitialized(true);
    }, [allowLocalRunners, availableRunners, environmentClasses, isLoadingClasses, isLoadingRunners]);

    return {
        isLoading: !initialized || isLoadingClasses || isLoadingRunners || isLoadingPolicies,
        environmentClassEntries,
    };
}

export function getCompareLabel(entry: EnvironmentClassEntry) {
    const label =
        `${entry?.runner?.name || ""},${entry?.clazz?.displayName}, ${entry?.clazz?.description}`.toLowerCase();
    return label;
}

export function getEnvironmentClassStatus(environmentClassEntry: EnvironmentClassEntry) {
    const policyRestricted = environmentClassEntry.policyRestricted;
    const offline =
        environmentClassEntry?.runner?.status?.phase !== RunnerPhase.ACTIVE &&
        environmentClassEntry?.runner?.status?.phase !== RunnerPhase.DEGRADED;
    const degraded = environmentClassEntry?.runner?.status?.phase === RunnerPhase.DEGRADED;
    return { policyRestricted, offline, degraded };
}

/**
 * Sort environment class entries by availability, degraded status, local runners, and CPU.
 *
 * First level of ordering:
 * 1. Active local runners
 * 2. Active remote runners
 * 3. Degraded runners (if available)
 * 4. Offline runners
 *
 * Second level of ordering:
 * 1. CPU (if available)
 * 2. Label
 */
export function sortEnvironmentClassEntries(entries: EnvironmentClassEntry[]): EnvironmentClassEntry[] {
    const getCPU = (entry: EnvironmentClassEntry) => {
        const instanceType = entry.clazz.configuration.find((c) => c.key === "instanceType")?.value;
        return instanceType ? vCPUbyInstanceType[instanceType] : undefined;
    };
    const isAvailable = (entry: EnvironmentClassEntry) => {
        const { policyRestricted, offline } = getEnvironmentClassStatus(entry);
        return !policyRestricted && !offline;
    };
    const isDegraded = (entry: EnvironmentClassEntry) => {
        const { degraded } = getEnvironmentClassStatus(entry);
        return degraded;
    };
    const isLocal = (entry: EnvironmentClassEntry) => {
        const { runner } = entry;
        return runner?.kind === RunnerKind.LOCAL;
    };
    return entries.sort((a, b) => {
        // Order by availability
        if (isAvailable(a) && !isAvailable(b)) {
            return -1;
        }
        if (!isAvailable(a) && isAvailable(b)) {
            return 1;
        }
        // Order by degraded status
        if (!isDegraded(a) && isDegraded(b)) {
            return -1;
        }
        if (isDegraded(a) && !isDegraded(b)) {
            return 1;
        }
        // Order by local runners
        if (isLocal(a) && !isLocal(b)) {
            return -1;
        }
        if (!isLocal(a) && isLocal(b)) {
            return 1;
        }
        // Order active ones by CPU if available
        const aCPU = getCPU(a);
        const bCPU = getCPU(b);
        if (aCPU && bCPU) {
            return aCPU - bCPU;
        }
        // Fallback to the label if the CPU is not available
        const aLabel = getCompareLabel(a);
        const bLabel = getCompareLabel(b);
        return aLabel.localeCompare(bLabel);
    });
}
